import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

// Define the Gallery component
const Gallery: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Define categories for filtering
  const categories = [
    { id: 'all', name: 'All Photos' },
    { id: 'landscape', name: 'Landscape' },
    { id: 'fishing', name: 'Fishing' },
    { id: 'wildlife', name: 'Wildlife' },
    { id: 'people', name: 'People' },
    { id: 'equipment', name: 'Equipment' }
  ];

  // Define photo data
  const photos = [
    {
      src: '/images/scenery 1.jpg',
      alt: 'Beautiful Loch Lomond Scenery',
      category: 'landscape'
    },
    {
      src: '/images/scenery2.jpg',
      alt: 'Stunning Mountain Views',
      category: 'landscape'
    },
    {
      src: '/images/scenery5.jpg',
      alt: 'Panoramic Loch Views',
      category: 'landscape'
    },
    {
      src: '/images/fishing111.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishing2.jpg',
      alt: 'Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishing3.jpg',
      alt: 'Fishing Techniques',
      category: 'fishing'
    },
    {
      src: '/images/fishing5.jpg',
      alt: 'Fishing Success',
      category: 'fishing'
    },
    {
      src: '/images/fishing7.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishing8.jpg',
      alt: 'Fishing Moment',
      category: 'fishing'
    },
    {
      src: '/images/fihsing9.jpg',
      alt: 'Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishing10.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat1.jpg',
      alt: 'Boat Fishing',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat2.jpg',
      alt: 'Boat Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat3.jpg',
      alt: 'Boat Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/outdoorrust.jpg',
      alt: 'Outdoor Adventure',
      category: 'landscape'
    }
  ];

  // Filter photos based on selected category
  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === selectedCategory);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Photo Gallery - Anchor Management</title>
        <meta name="description" content="Explore Anchor Management's collection of stunning fishing and landscape photos from Loch Lomond" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/scenery 1.jpg"
            alt="Gallery hero image"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-4"
          >
            Photo Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-2xl mx-auto"
          >
            Capturing the beauty and excitement of fishing at Loch Lomond
          </motion.p>
        </div>
        
        {/* Wave Shape */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            className="w-full h-auto"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#00B4D8"
              fillOpacity="0.8"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPhotos.map((photo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px", amount: 0.3 }}
                transition={{ 
                  duration: 1.2,
                  delay: index * 0.05,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg shadow-lg"
              >
                <motion.img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <motion.p 
                      className="text-sm font-medium"
                      initial={{ y: 10, opacity: 0 }}
                      whileHover={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {photo.alt}
                    </motion.p>
                    <motion.p 
                      className="text-xs text-gray-300 capitalize"
                      initial={{ y: 10, opacity: 0 }}
                      whileHover={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {photo.category}
                    </motion.p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Export the Gallery component as default
export default Gallery; 